import { BACKGROUND_COLORS } from '../../types';

/**
 * BackgroundPicker - Background color selection
 *
 * Features:
 * - 5 therapeutic background colors
 * - Visual color swatches (border + circle style)
 * - Pro gating for premium colors
 * - Matches ColorPicker visual style
 */

interface BackgroundPickerProps {
  value: string;
  onChange: (color: string) => void;
  disabled?: boolean;
  isPro: boolean;
}

export function BackgroundPicker({
  value,
  onChange,
  disabled = false,
  isPro,
}: BackgroundPickerProps): JSX.Element {
  // Check if current color is a custom color (not in presets)
  const isCustomColor = !BACKGROUND_COLORS.some(preset => preset.value === value);

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        Background
      </label>

      <div className="flex flex-wrap gap-2">
        {BACKGROUND_COLORS.map((preset) => {
          const isLocked = preset.isPro && !isPro;
          const isSelected = value === preset.value;

          return (
            <button
              key={preset.value}
              type="button"
              onClick={() => !isLocked && !disabled && onChange(preset.value)}
              disabled={disabled || isLocked}
              className={`
                relative w-[60px] h-[60px] rounded border-2 transition-all flex-shrink-0
                ${isSelected ? 'border-gray-900 ring-2 ring-gray-900 ring-offset-1' : 'border-gray-300'}
                ${isLocked ? 'opacity-40 cursor-not-allowed' : 'hover:scale-105 cursor-pointer'}
                ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
              `}
              style={{ backgroundColor: preset.value }}
              title={preset.name}
              aria-label={`Select ${preset.name} background`}
            >
              {/* Pro icon */}
              {isLocked && (
                <div className="absolute top-1 right-1 text-xs pro-icon"></div>
              )}
            </button>
          );
        })}

        {/* Custom color picker - Pro only */}
        <button
          type="button"
          className={`
            relative w-[60px] h-[60px] rounded border-2 transition-all flex-shrink-0
            ${isCustomColor ? 'border-gray-900 ring-2 ring-gray-900 ring-offset-1' : 'border-gray-300'}
            ${!isPro ? 'opacity-40 cursor-not-allowed' : 'hover:scale-105 cursor-pointer'}
            ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          `}
          style={{ backgroundColor: isCustomColor ? value : '#f3f4f6' }}
          title="Custom background color"
          disabled={disabled || !isPro}
        >
          {/* Hidden color input */}
          {isPro && (
            <input
              type="color"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              disabled={disabled}
              className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
              title="Custom background color picker"
            />
          )}

          {/* Artist palette icon overlay */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span className="text-2xl drop-shadow-sm">🎨</span>
          </div>

          {/* Pro icon */}
          {!isPro && <span className="absolute top-1 right-1 text-xs pro-icon"></span>}
        </button>
      </div>
    </div>
  );
}
